/**
 * Moldura visual comum a todas as telas.
 *
 * Papel: este componente desenha a barra superior — marca à esquerda e, à
 * direita, o grupo com os links de administração, o nome de quem está logado,
 * o botão de sair e a trilha de navegação —, e é dentro do `Outlet` dele que a
 * página da rota ativa é renderizada. Como toda rota declarada no `router.tsx` é filha da rota `/`,
 * que é quem monta este Layout, tudo o que estiver aqui aparece em todas as
 * telas.
 *
 * Como usar: a página de um módulo não deverá desenhar cabeçalho próprio nem
 * abrir o próprio container de largura, porque o `<main>` daqui já ocupa a
 * tela inteira, com 32 px de respiro nas laterais e nenhum respiro vertical —
 * o primeiro elemento da página nasce encostado à borda inferior da barra
 * superior. Uma página que abrir o próprio container acabará com o
 * espaçamento dobrado, ou com uma largura diferente da que as outras telas
 * têm; uma página que precisar de respiro vertical deverá compor essa margem
 * no próprio conteúdo — no título ou na primeira seção —, e não pedir isso ao
 * `<main>`.
 *
 * A única exceção é o `QuadroEmbutido` de `@/shared/embutido`, onde Finanças e
 * Manutenções desenham a tela da origem: ele desfaz os 32 px laterais com
 * `-mx-8` para ir de borda a borda, como o `<iframe>` da origem ia. Mudar o
 * respiro do `<main>` exigirá mudar o `-mx-8` do quadro na mesma alteração,
 * como já exige nas duas folhas de impressão.
 *
 * O `FolhaDaEquipe` e o `FolhaDoCalendario` desfazem o `px-8` do `<main>` na
 * impressão, com `print:-mx-8` e a largura somada dos mesmos 4rem, para que a
 * folha comece na margem do papel. Mudar esse respiro aqui exigirá mudar as
 * duas folhas na mesma alteração; mudado só aqui, a folha impressa sai
 * deslocada e cortada numa das bordas.
 *
 * A trilha é montada a partir do `MODULOS`, comparando o começo do endereço
 * atual com o campo `rota` de cada módulo. Quando uma rota nova for criada no
 * `router.tsx` e a trilha continuar sem aparecer, o motivo será o campo
 * `rota` daquele módulo em `modulos.ts` estar vazio ou diferente do `path`
 * que foi declarado. Ela é o último item do grupo à direita da barra, depois
 * dos links de administração, do nome e do botão de sair, e separada deles por
 * uma barra vertical decorativa; a trilha e essa barra separadora somem juntas em qualquer
 * largura de 560 px ou menos, porque abaixo dela o espaço que resta não
 * comporta a marca e o grupo inteiro na mesma linha sem provocar rolagem
 * horizontal.
 *
 * O último item da trilha é um link, e não texto solto, quando o módulo
 * encontrado tem `assuntoDe`: é o caso do Calendário e das Manutenções da
 * Fazenda Boa Vista, que mostram "Residências" levando a `/residencias` em vez
 * do próprio nome, porque os dois vivem como submenu dentro daquela tela e não
 * têm trilha própria. Em `/residencias`, o módulo encontrado não tem
 * `assuntoDe`, e o rótulo aparece como texto solto, sem link — é a própria
 * tela em que já se está.
 *
 * Os links de Cadastros-base e de Usuários, à direita da barra, dependem do
 * `useAcesso`: enquanto a consulta não responde, ou quando ela falha, os dois
 * ficam fora da tela — a `data` chega `undefined` nos dois casos, e é essa
 * ausência que a comparação com `acessoAlcanca` testa. Um deles nunca aparece
 * antes do outro nem some sozinho por instabilidade de rede: os dois nascem e
 * somem juntos com a mesma consulta.
 *
 * A marca e o grupo da direita somados passam da largura da janela bem antes
 * de qualquer um deles precisar sumir sozinho — por isso a barra é
 * `flex-wrap`, e o grupo da direita, que reúne os links de administração, o
 * nome, o botão de sair, a barra separadora e a trilha, também é `flex-wrap` com
 * `justify-end`, para que cada linha em que ele quebrar continue rente à
 * borda direita em vez de nascer colada à esquerda. Um link novo somado a
 * esse grupo sem manter os dois `flex-wrap` volta a produzir rolagem
 * horizontal na janela estreita, porque some o único mecanismo que resolve a
 * barra não caber numa linha só.
 *
 * O nome de quem está logado é texto, e não link: ele identifica a conta
 * aberta antes de alguém clicar em sair, sem levar a tela nenhuma. O nome longo
 * é cortado por `truncate`, e o `min-w-0` ao lado dele é o que faz o corte
 * valer: sem essa declaração, a largura mínima automática de um item flex é o
 * tamanho do próprio conteúdo, e o `max-w-40 truncate` nunca chega a entrar em
 * ação — o nome empurra o botão de sair para fora da barra em vez de truncar.
 *
 * O `<header>` leva `print:hidden`: uma tela que manda imprimir — o orçamento,
 * o pedido de compra, a folha da equipe e a folha do calendário, por exemplo —
 * não pode sair com a marca e a navegação por cima do que a pessoa quis
 * imprimir. O fundo em branco na impressão é regra de `body`, em
 * `index.css`, e não daqui.
 *
 * O `PopupVencimentosDaAbertura` é montado aqui, ao lado do `<main>`, e não na
 * `PaginaInicial`, porque este é o componente que nasce uma vez por
 * carregamento da página e continua montado enquanto a pessoa navega. Levado
 * para dentro de uma página, ele nasceria de novo a cada visita a ela, e a
 * caixa de vencimentos voltaria a abrir toda vez que alguém entrasse naquela
 * tela.
 */
import { useAuth0 } from '@auth0/auth0-react';
import { ROTULOS_MODULO, acessoAlcanca } from '@concierge/shared';
import { useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router';

import { useAcesso } from '@/shared/hooks';

import { BotaoDeVencimentos } from './botao-de-vencimentos';
import { MODULOS } from './modulos';
import { PopupVencimentosDaAbertura } from './popup-vencimentos-da-abertura';
import { soTemSolicitacaoDeCompra } from './so-solicitacao-de-compra';

/** O que a trilha mostra ao lado da marca: o rótulo e, quando houver, o link. */
interface InfoDaTrilha {
  /** O nome mostrado depois da barra. */
  rotulo: string;
  /** Rota do link, ou `null` quando o item final deve ser texto solto. */
  linkPara: string | null;
}

/**
 * Descobre o que a trilha deve mostrar para o endereço atual.
 *
 * A busca percorre o `MODULOS` e encontra o primeiro cujo campo `rota` for o
 * começo do caminho recebido. Os módulos com a `rota` vazia ficam de fora da
 * comparação, porque uma string vazia é o começo de qualquer caminho e o
 * primeiro deles acabaria casando com todas as telas.
 *
 * Quando o módulo encontrado tem `assuntoDe`, o rótulo devolvido é o do módulo
 * pai, em `ROTULOS_MODULO`, e `linkPara` é a `rota` dele — é assim que
 * `/calendario` e `/manutencoes` mostram "Residências" como link em vez do
 * próprio nome. Sem `assuntoDe`, o rótulo é o do próprio módulo e `linkPara`
 * vem `null`, porque a tela em que já se está não precisa de link para si.
 *
 * O retorno será `null` na tela inicial e em qualquer endereço que não pertença
 * a um módulo conhecido, e nesses casos a trilha inteira deixa de ser
 * renderizada, em vez de aparecer com um rótulo vazio ao lado da marca.
 *
 * @param caminho - Caminho atual, como o `useLocation().pathname` devolve.
 * @returns A informação da trilha, ou `null` quando nenhum módulo casar.
 */
function infoDaTrilha(caminho: string): InfoDaTrilha | null {
  const modulo = MODULOS.find((m) => m.rota !== '' && caminho.startsWith(m.rota));
  if (modulo === undefined) return null;

  if (modulo.assuntoDe === undefined) {
    return { rotulo: ROTULOS_MODULO[modulo.chave], linkPara: null };
  }

  const moduloPai = MODULOS.find((m) => m.chave === modulo.assuntoDe);
  return { rotulo: ROTULOS_MODULO[modulo.assuntoDe], linkPara: moduloPai?.rota ?? null };
}

/**
 * Moldura comum a todas as telas, com barra superior e área de conteúdo.
 *
 * Quem monta este componente é o `LayoutProtegido`, na rota `/` do
 * `router.tsx`, e por isso ele não recebe nenhuma prop: tudo o que ele precisa
 * vem do endereço atual, pelo `useLocation`, da sessão aberta, pelo
 * `useAuth0`, e do acesso concedido, pelo `useAcesso`.
 *
 * @returns A barra superior da aplicação, o `Outlet` onde a página da rota
 *   ativa é renderizada e a caixa de vencimentos da abertura.
 */
export function Layout() {
  const { pathname } = useLocation();
  const { user, logout } = useAuth0();
  const acesso = useAcesso();
  const trilha = infoDaTrilha(pathname);
  const [caixaPeloBotao, setCaixaPeloBotao] = useState(false);

  // Quem só tem a solicitação de compra vê a moldura mínima (marca, nome da tela
  // e Sair): o sino e a caixa de vencimentos consultariam documentos e
  // manutenções que essa pessoa não abre.
  const soSolicitacao = acesso.data !== undefined && soTemSolicitacaoDeCompra(acesso.data);
  const podeVerCadastrosBase =
    acesso.data !== undefined && acessoAlcanca(acesso.data, 'cadastros-base', 'ver');
  const podeVerUsuarios =
    acesso.data !== undefined && acessoAlcanca(acesso.data, 'usuarios', 'ver');

  /**
   * Encerra a sessão no Auth0 e devolve o navegador para a raiz do site.
   *
   * O destino é a própria aplicação, e não a tela de despedida do Auth0, porque
   * a raiz é protegida pelo `LayoutProtegido` e leva direto ao login — que é
   * para onde quem acabou de sair costuma querer ir, para entrar com outra
   * conta.
   */
  function handleSair() {
    void logout({ logoutParams: { returnTo: window.location.origin } });
  }

  return (
    <div className="flex min-h-screen flex-col">
      <header className="border-b border-border print:hidden">
        <div className="flex flex-wrap items-center gap-3 px-8 py-5">
          <Link
            to="/"
            className="font-sans text-[26px] font-bold tracking-[0.01em] text-brass"
          >
            FAMÍLIA PRADO
          </Link>

          <div className="ml-auto flex flex-wrap items-center justify-end gap-x-4 gap-y-2 font-mono text-xs text-texto-fraco">
            {!soSolicitacao && (
              <>
                <BotaoDeVencimentos onAbrir={() => setCaixaPeloBotao(true)} />
                {podeVerCadastrosBase && (
                  <Link
                    to="/cadastros-base"
                    className="transition-colors hover:text-brass-light"
                  >
                    {ROTULOS_MODULO['cadastros-base']}
                  </Link>
                )}
                {podeVerUsuarios && (
                  <Link to="/usuarios" className="transition-colors hover:text-brass-light">
                    Usuários
                  </Link>
                )}
                {/* `name` do Auth0 cai no e-mail quando o perfil não traz nome; sem
                    nenhum dos dois, o nome sai da barra em vez de ocupar espaço vazio. */}
                {user?.name !== undefined && (
                  <span className="max-w-40 min-w-0 truncate">{user.name}</span>
                )}
              </>
            )}
            <button
              type="button"
              onClick={handleSair}
              className="rounded-sm transition-colors hover:text-brass-light focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
            >
              Sair
            </button>

            {trilha && soSolicitacao && (
              <span className="font-mono text-xs text-foreground">{trilha.rotulo}</span>
            )}

            {trilha && !soSolicitacao && (
              <>
                <span
                  aria-hidden="true"
                  className="h-4 w-px bg-linha-forte max-[561px]:hidden"
                />
                <nav
                  aria-label="Trilha de navegação"
                  className="flex items-center gap-2 max-[561px]:hidden"
                >
                  <Link
                    to="/"
                    className="text-muted-foreground transition-colors hover:text-brass-light"
                  >
                    Início
                  </Link>
                  <span aria-hidden="true">/</span>
                  {trilha.linkPara === null ? (
                    <span className="text-foreground">{trilha.rotulo}</span>
                  ) : (
                    <Link
                      to={trilha.linkPara}
                      className="text-muted-foreground transition-colors hover:text-brass-light"
                    >
                      {trilha.rotulo}
                    </Link>
                  )}
                </nav>
              </>
            )}
          </div>
        </div>
      </header>

      <main className="flex flex-1 flex-col px-8">
        <Outlet />
      </main>

      {!soSolicitacao && (
        <PopupVencimentosDaAbertura
          abertaPeloBotao={caixaPeloBotao}
          onFecharPeloBotao={() => setCaixaPeloBotao(false)}
        />
      )}
    </div>
  );
}
